<script setup>
import { ref, computed, watch } from 'vue';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import { ATTACHMENT_TYPES } from '../constants';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';

const emit = defineEmits(['error']);
const hasError = ref(false);
const showGallery = ref(false);
const loadRetries = ref(0);
const { filteredCurrentChatAttachments, attachments } = useMessageContext();

const attachment = computed(() => {
  return attachments.value[0];
});

const sourceUrl = ref(attachment.value?.dataUrl);

// A freshly landed video (received, or just sent) can 404 for an instant
// while the file is still settling in storage; <video> never retries on its
// own. Reload with a fresh cache-buster a few times before giving up, same
// pattern already used for audio (see chips/Audio.vue#onLoadError).
const handleError = () => {
  const url = attachment.value?.dataUrl;
  if (url && loadRetries.value < 3) {
    loadRetries.value += 1;
    setTimeout(() => {
      sourceUrl.value = timeStampAppendedURL(url);
    }, 1000 * loadRetries.value);
    return;
  }
  hasError.value = true;
  emit('error');
};

// The optimistic (pending) message renders this bubble before the attachment
// has a dataUrl at all. Reset once a (new) URL arrives so the bubble recovers
// on its own instead of staying stuck until a reload.
watch(
  () => attachment.value?.dataUrl,
  newUrl => {
    hasError.value = false;
    loadRetries.value = 0;
    sourceUrl.value = newUrl;
  }
);

const isReel = computed(() => {
  return attachment.value.fileType === ATTACHMENT_TYPES.IG_REEL;
});
</script>

<template>
  <BaseBubble
    class="overflow-hidden p-3"
    data-bubble-name="video"
    @click="showGallery = true"
  >
    <div class="relative group rounded-md overflow-hidden">
      <div
        v-if="isReel"
        class="absolute p-2 flex items-start justify-end right-0 pointer-events-none"
      >
        <Icon icon="i-lucide-instagram" class="text-white shadow-lg" />
      </div>
      <video
        controls
        class="rounded-md skip-context-menu"
        :src="sourceUrl"
        :class="{
          'max-w-48': isReel,
          'max-w-full': !isReel,
        }"
        @error="handleError"
      />
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="onError"
    @close="() => (showGallery = false)"
  />
</template>
